import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  PlayArrow,
  CheckCircle,
  LocationCity,
  Flight,
  Group,
  Phone,
  School,
  AttachMoney,
  Star,
} from '@mui/icons-material';
import Navigation from './Navigation';

const Coaching: React.FC = () => {
  const benefits = [
    { icon: <LocationCity />, text: 'How to Navigate Bay Area Housing, Tech Culture & Cost of Living' },
    { icon: <Group />, text: 'Elite Network of Bay Area Professionals & Adventure Travelers' },
    { icon: <Flight />, text: 'Ultimate Travel Guides to Costa Rica, Mexico & Canada from SF' },
    { icon: <CheckCircle />, text: 'Bay Area Job Market & Tech Industry Insider Tips Posted Monthly!' },
    { icon: <Phone />, text: 'Private Calls with Jessica, once a month for personalized guidance' },
    { icon: <School />, text: '15+ hours of Exclusive Bay Area Living & Travel Planning Training' },
    { icon: <Star />, text: 'VIP Resources: SF/Peninsula/South Bay Housing, BART Navigation, Best Neighborhoods' },
    { icon: <Flight />, text: 'Curated Vacation Itineraries: Costa Rica Adventures, Mexico Beach Escapes & Canadian City Breaks' },
    { icon: <AttachMoney />, text: 'How to Maximize Bay Area Salary for Travel & Build Wealth in High-Cost Area' },
  ];

  const cities = [
    { name: 'San Francisco', image: '/api/placeholder/300/200' },
    { name: 'Costa Rica', image: '/api/placeholder/300/200' },
    { name: 'Mexico', image: '/api/placeholder/300/200' },
    { name: 'Canada', image: '/api/placeholder/300/200' },
  ];

  return (
    <Box>
      <Navigation />
      
      {/* Hero Section */}
      <Box sx={{ backgroundColor: 'primary.main', color: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" textAlign="center" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
            Join the #1 Bay Area Living & Adventure Travel Network
          </Typography>
          <Typography variant="h5" textAlign="center" sx={{ mb: 4 }}>
            Master Silicon Valley life & epic getaways 🌉
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                backgroundColor: 'white',
                color: 'primary.main',
                px: 6,
                py: 2,
                fontSize: '1.3rem',
                fontWeight: 'bold',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              Apply Now
            </Button>
          </Box>
        </Container>
      </Box>

      {/* VSL Video Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 'bold', mb: 6 }}>
          Watch My Story
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <Card sx={{ maxWidth: 800, boxShadow: 4 }}>
            <CardMedia
              sx={{ height: 450, position: 'relative', cursor: 'pointer' }}
              image="/api/placeholder/800/450"
              title="VSL Video"
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'rgba(33, 150, 243, 0.9)',
                  borderRadius: '50%',
                  width: 100,
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PlayArrow sx={{ fontSize: 50, color: 'white' }} />
              </Box>
            </CardMedia>
          </Card>
        </Box>
        
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            sx={{
              px: 8,
              py: 2,
              fontSize: '1.2rem',
              fontWeight: 'bold',
              textTransform: 'none',
            }}
          >
            Apply Now - Transform Your Life
          </Button>
        </Box>
      </Container>

      {/* City Photos Section */}
      <Box sx={{ backgroundColor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 'bold', mb: 6 }}>
            From Silicon Valley to World Adventures
          </Typography>
          <Grid container spacing={4}>
            {cities.map((city, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{ boxShadow: 3, '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' }, transition: 'all 0.3s' }}>
                  <CardMedia
                    sx={{ height: 200 }}
                    image={city.image}
                    title={city.name}
                  />
                  <CardContent>
                    <Typography variant="h6" textAlign="center" sx={{ fontWeight: 'bold' }}>
                      {city.name}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                px: 8,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 'bold',
                textTransform: 'none',
              }}
            >
              Start Your Journey - Apply Now
            </Button>
          </Box>
        </Container>
      </Box>

      {/* What You Get Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 'bold', mb: 6 }}>
          You'll gain instant access to:
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <List>
              {benefits.map((benefit, index) => (
                <ListItem key={index} sx={{ mb: 2 }}>
                  <ListItemIcon sx={{ color: 'primary.main' }}>
                    {benefit.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={benefit.text} 
                    sx={{ 
                      '& .MuiListItemText-primary': { 
                        fontSize: '1.1rem',
                        fontWeight: 500,
                      } 
                    }} 
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center', backgroundColor: '#f8f9fa' }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                Limited Time Offer
              </Typography>
              <Chip 
                label="BONUS INCLUDED" 
                color="success" 
                sx={{ mb: 3, fontSize: '1rem', fontWeight: 'bold' }} 
              />
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                Seasonal travel calendar with best times to visit each destination, flight deal alerts from SFO/OAK/SJC, and exclusive discounts with trusted travel partners.
              </Typography>
              <Button
                variant="contained"
                fullWidth
                size="large"
                sx={{
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  textTransform: 'none',
                }}
              >
                Apply Now
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Final CTA Section */}
      <Box sx={{ backgroundColor: 'primary.main', color: 'white', py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h4" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
            Transform from Bay Area newcomer to confident local
          </Typography>
          <Typography variant="h6" textAlign="center" sx={{ mb: 6, lineHeight: 1.6 }}>
            Who knows how to thrive professionally AND escape for amazing adventures in Costa Rica, Mexico & Canada.
          </Typography>
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                backgroundColor: 'white',
                color: 'primary.main',
                px: 8,
                py: 3,
                fontSize: '1.3rem',
                fontWeight: 'bold',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              Apply Now - Start Your Transformation
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Coaching;